import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  ModalContentProps,
  Select,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FileRow } from './components/FileRow';
import { useFileUpload } from './hooks/useFIleUpload';
import { ModalFrame } from 'components/modal/ModalFrame';
import useApplicationContext from 'contexts/application/useApplicationContext';

const modalStyle: ModalContentProps = {
  maxW: '700px',
  minW: '700px',
  marginTop: 100,
};

export interface UploadFileModalProps {
  isOpen: boolean;
  onClose: ()=> void;
}

export function UploadFileModal({
  isOpen,
  onClose,
}: UploadFileModalProps): JSX.Element {
  const { application, refetchApplication } = useApplicationContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedApplicantId, setApplicantId] = useState<number | undefined>();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const { applicantId } = useParams();

  const upload = useFileUpload({
    applicantId: selectedApplicantId,
    applicationId: application.id!,
  });

  const matchApplicantId = application.applicants?.find((a) => a.id === Number(applicantId));

  useEffect(() => {
    if (matchApplicantId) {
      setApplicantId(matchApplicantId.id);
    }
  }, [application.applicants, matchApplicantId]);

  const disableForm = upload.uploading;

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) upload.addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const onUpload = async (): Promise<void> => {
    setTriedSubmit(true);
    const uploadCompleted = await upload.uploadAllFiles();

    if (uploadCompleted) {
      await refetchApplication();
      onClose();
    }
  };

  const fileCount = Object.keys(upload.docs).length;

  const Header = (
    <Box
      fontSize='35px' textAlign='center'
      borderRadius='10px'
    >
      Upload File
    </Box>
  );

  return (
    <ModalFrame
      hasCloseButton={!disableForm}
      style={{ content: modalStyle }}
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      header={Header}
    >
      <Box
        opacity={disableForm ? 0.5 : 1}
        pointerEvents={disableForm ? 'none' : 'auto'}
      >
        <FormControl isInvalid={triedSubmit && !selectedApplicantId} mb={4}>
          <Text fontSize='sm'>Applicant:</Text>
          <Select
            placeholder='Select an applicant'
            value={selectedApplicantId ?? ''}
            onChange={(e) => setApplicantId(Number(e.target.value))}
            border='2px solid'
            borderColor='gray.400'
            borderRadius='12px'
            focusBorderColor='red.400'
            h='55px'
            mt={2}
            textTransform='capitalize'
          >
            {application.applicants?.map((a) => (
              <option key={a.id} value={a.id}>
                {_.capitalize(a.firstName)} {_.capitalize(a.lastName)}
              </option>
            ))}
          </Select>
          <FormErrorMessage>Applicant is required</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={triedSubmit && fileCount === 0}>
          <Input
            ref={fileInputRef}
            type='file'
            multiple
            accept='application/pdf,image/*'
            onChange={onFileChange}
            display='none'
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            bg='black'
            color='white'
            _hover={{ bg: 'gray.700' }}
            borderRadius='12px'
            h='55px'
            w='100%'
            mt={0}
            borderColor={
              triedSubmit && fileCount === 0 ? 'red.400' : 'gray.500'
            }
          >
            Select Files
          </Button>
          <FormErrorMessage>Please choose at least one file</FormErrorMessage>
        </FormControl>
        <Box
          mt={2} maxH='400px'
          overflowY='auto' pr={4}
          mb={4}
        >
          {Object.values(upload.docs).map((d) => (
            <FileRow
              key={d.file.name}
              fileName={d.file.name}
              fileType={d.type}
              showError={triedSubmit}
              onRemove={upload.removeFiles}
              onTypeChange={upload.updateFileType}
            />
          ))}
        </Box>
        <Button
          onClick={onUpload}
          isLoading={upload.uploading}
          loadingText='Uploading…'
          mb={3}
        >
          Upload
        </Button>
      </Box>
    </ModalFrame>
  );
}
